/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-02-14 14:30:58
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field } from 'redux-form';
import { Popover, FormLayout, DatePicker, Icon } from '@shopify/polaris';
import { CalendarMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';

import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { DATE_FORMAT } from 'src/constants/language-setting';
import { isRequired } from 'src/helpers/form-validation-helper';
import { getStoreDate } from 'src/helpers/date-time-helper';

const isRequiredValue = isRequired();

type Props = WithI18nProps;

type State = {
  popoverActive: boolean,
  month: number,
  year: number,
  selectedDates: {
    start: Date | null,
    end: Date | null
  }
};

class RenderDatePickerComponent extends Component<Props, State> {
  today = getStoreDate(new Date());

  state = {
    popoverActive: false,
    month: this.today.getMonth(),
    year: this.today.getFullYear(),
    selectedDates: {
      start: null,
      end: null
    }
  };

  componentDidUpdate() {
    // Init selected date for first loading
    let { selectedDates } = this.state;
    const { start } = selectedDates;
    const { appLanguage, selectedDate } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;

    // Having date
    if (!start && selectedDate) {
      selectedDates.start = moment(selectedDate, formatDateNumber).toDate();
      selectedDates.end = moment(selectedDate, formatDateNumber).toDate();
      this.setState({ selectedDates });
    }

    // First time of picking
    if (!start && !selectedDate) {
      const date: any = getStoreDate(new Date());
      selectedDates.start = date;
      selectedDates.end = date;
      this.setState({ selectedDates });
    }
  }

  render() {
    const { popoverActive, month, year, selectedDates } = this.state;
    const { name, i18n, disabled } = this.props;

    const activator = (
      <div className="date-select">
        <Field
          anotherProps={{ prefix: <Icon source={CalendarMajor} />, disabled }}
          name={name}
          component={RenderTextField}
          label={i18n.translate('AdminHTML.Sync.SyncSchedule.date_Label')}
          onFocus={this.openPopoverActive}
          validate={[isRequiredValue, this.isValidDate]}
          onChange={this.changeDate}
        />
      </div>
    );

    return (
      <div className="">
        <Popover
          active={popoverActive}
          activator={activator}
          onClose={this.closePopoverActive}
          sectioned>
          <Popover.Pane>
            <Popover.Section>
              <FormLayout>
                <div>
                  <DatePicker
                    month={month}
                    year={year}
                    onChange={this.setSelectedDates}
                    onMonthChange={this.handleMonthChange}
                    selected={selectedDates}
                    multiMonth={false}
                    allowRange={false}
                    disableDatesAfter={moment(this.today).add(30, 'day').toDate()}
                    disableDatesBefore={this.today}
                  />
                </div>
              </FormLayout>
            </Popover.Section>
          </Popover.Pane>
        </Popover>
      </div>
    );
  }

  // Toggle state to show/hide the popover
  closePopoverActive = () => {
    this.setState({ popoverActive: false });
  };

  openPopoverActive = () => {
    this.setState({ popoverActive: true });
  };

  // Handle change date
  setSelectedDates = selectedDates => {
    const { appLanguage, updateField } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    const date = moment(selectedDates.start).format(formatDateNumber);
    // Save object date
    this.setState({ selectedDates });

    updateField('selectedDate', date);

    // Close popover
    this.closePopoverActive();
  };

  // Handle change month
  handleMonthChange = (month, year) => {
    this.setState({ month, year });
  };

  changeDate = value => {
    const { appLanguage } = this.props;

    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    if (moment(value, formatDateNumber).isValid()) {
      const date = moment(value, formatDateNumber).toDate();
      this.setState({ selectedDates: { start: date, end: date } });
    }
  };

  isValidDate = value => {
    const { appLanguage, i18n } = this.props;

    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    if (!moment(value, formatDateNumber).isValid()) {
      return i18n.translate('AdminHTML.Sync.SyncSchedule.isNotDate');
    } else {
      const date = moment(value, formatDateNumber);
      if (date.diff(moment(this.today)) < 0 || date.diff(moment(this.today).add(30, 'day')) > 0) {
        return i18n.translate('AdminHTML.Sync.SyncSchedule.invalidDate');
      }
    }
  };
}

const mapStateToProps = (state, props) => ({
  appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE)
});

export const RenderDatePicker = withI18n()(connect(mapStateToProps, {})(RenderDatePickerComponent));
